import React from 'react';

/**
 * Get urgency class based on due date
 */
function getUrgencyClass(dueDate) {
  if (!dueDate) return 'border-gray-300';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'border-red-500'; // Overdue
  if (diffDays === 0) return 'border-red-500'; // Due today
  if (diffDays <= 3) return 'border-orange-500'; // Due within 3 days
  if (diffDays <= 7) return 'border-yellow-500'; // Due within 7 days
  return 'border-gray-300'; // No urgency
}

/**
 * Format due date for display - very compact
 */
function formatDueDate(dueDate) {
  if (!dueDate) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return `${Math.abs(diffDays)}d late`;
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1d';
  if (diffDays <= 14) return `${diffDays}d`;

  return due.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

/**
 * Get text color class for due date
 */
function getDueDateColorClass(dueDate) {
  if (!dueDate) return 'text-gray-500';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);

  const diffDays = Math.ceil((due - today) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'text-red-400';
  if (diffDays === 0) return 'text-red-400';
  if (diffDays <= 3) return 'text-orange-400';
  if (diffDays <= 7) return 'text-yellow-400';
  return 'text-gray-500';
}

/**
 * Compact 2-line Quote card
 * Line 1: Quote# + Project Name
 * Line 2: PM, Customer
 * Right: X/Y SKUs
 */
export default function QuoteGroupCard({ group }) {
  const urgencyBorderClass = getUrgencyClass(group.earliestDue);
  const skuCount = group.jobs?.length || 0;
  const totalSkus = group.totalSkusInQuote || skuCount;
  const dueText = formatDueDate(group.earliestDue);
  const dueDateClass = getDueDateColorClass(group.earliestDue);

  // Build line 2 parts (PM, Customer)
  const line2Parts = [];
  if (group.projectManager) line2Parts.push(group.projectManager);
  if (group.customerCode) line2Parts.push(group.customerCode);

  return (
    <div className={`bg-slate-100 rounded border-l-2 ${urgencyBorderClass} px-1.5 py-1 flex`}>
      {/* Left: Quote info */}
      <div className="flex-1 min-w-0">
        {/* Line 1: Quote# + Project */}
        <div className="flex items-center gap-1 text-xs">
          <span className="font-mono font-medium text-gray-900 shrink-0">{group.quoteNumber || '?'}</span>
          {group.projectName && (
            <span className="text-gray-700 truncate" title={group.projectName}>
              {group.projectName}
            </span>
          )}
        </div>
        {/* Line 2: PM, Customer */}
        {line2Parts.length > 0 && (
          <div className="text-xs text-gray-500 truncate" title={line2Parts.join(' • ')}>
            {line2Parts.join(' • ')}
          </div>
        )}
      </div>

      {/* Right: SKU count + due */}
      <div className="shrink-0 text-right ml-1.5 flex flex-col justify-center">
        <div className="text-xs text-gray-700 font-medium whitespace-nowrap">
          {skuCount}/{totalSkus}
        </div>
        {dueText && (
          <div className={`text-xs ${dueDateClass} whitespace-nowrap`}>
            {dueText}
          </div>
        )}
      </div>
    </div>
  );
}

// Export helper functions for use in other components
export { getUrgencyClass, formatDueDate, getDueDateColorClass };
